package ir.alirezaivaz.finglish.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExtendedFloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.unit.LayoutDirection
import finglish.composeapp.generated.resources.Res
import finglish.composeapp.generated.resources.action_clear
import finglish.composeapp.generated.resources.action_copy
import finglish.composeapp.generated.resources.action_more_options
import finglish.composeapp.generated.resources.action_paste
import finglish.composeapp.generated.resources.action_settings
import finglish.composeapp.generated.resources.action_share
import finglish.composeapp.generated.resources.app_name
import finglish.composeapp.generated.resources.error_something_wrong
import finglish.composeapp.generated.resources.ic_clear
import finglish.composeapp.generated.resources.ic_copy
import finglish.composeapp.generated.resources.ic_more_vert
import finglish.composeapp.generated.resources.ic_paste
import finglish.composeapp.generated.resources.ic_settings
import finglish.composeapp.generated.resources.ic_share
import ir.alirezaivaz.finglish.ui.sheets.SettingsSheet
import ir.alirezaivaz.finglish.ui.theme.Dimens
import ir.alirezaivaz.finglish.ui.theme.montserratFontFamily
import ir.alirezaivaz.finglish.ui.theme.vazirmatnFontFamily
import ir.alirezaivaz.finglish.ui.widgets.FinglishTopBar
import ir.alirezaivaz.finglish.utils.FinglishConverter
import ir.alirezaivaz.finglish.utils.SettingsManager
import ir.alirezaivaz.finglish.utils.Utils
import ir.alirezaivaz.kartam.extensions.handPointerIcon
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.getString
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MainScreen(
    modifier: Modifier = Modifier,
) {
    val scope = rememberCoroutineScope()
    val clipboardManager = LocalClipboardManager.current
    var input by remember { mutableStateOf("") }
    val output = FinglishConverter.convert(input)
    var showOptionsMenu by remember { mutableStateOf(false) }
    var showSettingsSheet by remember { mutableStateOf(false) }
    val snackbarHostState = remember { SnackbarHostState() }

    Scaffold(
        modifier = modifier
            .fillMaxSize()
            .imePadding(),
        snackbarHost = {
            SnackbarHost(
                hostState = snackbarHostState
            )
        },
        topBar = {
            FinglishTopBar(
                title = stringResource(Res.string.app_name),
                actions = {
                    IconButton(
                        modifier = Modifier.handPointerIcon(),
                        onClick = {
                            input = ""
                        }
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_clear),
                            contentDescription = stringResource(Res.string.action_clear)
                        )
                    }
                    IconButton(
                        modifier = Modifier.handPointerIcon(),
                        onClick = {
                            input = clipboardManager.getText()?.text.toString()
                        }
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_paste),
                            contentDescription = stringResource(Res.string.action_paste)
                        )
                    }
                    IconButton(
                        modifier = Modifier.handPointerIcon(),
                        onClick = {
                            showOptionsMenu = true
                        }
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_more_vert),
                            contentDescription = stringResource(Res.string.action_more_options)
                        )
                    }
                    DropdownMenu(
                        expanded = showOptionsMenu,
                        onDismissRequest = {
                            showOptionsMenu = false
                        }
                    ) {
                        DropdownMenuItem(
                            modifier = Modifier.handPointerIcon(),
                            text = {
                                Text(
                                    text = stringResource(Res.string.action_settings)
                                )
                            },
                            leadingIcon = {
                                Icon(
                                    painter = painterResource(Res.drawable.ic_settings),
                                    contentDescription = stringResource(Res.string.action_settings)
                                )
                            },
                            onClick = {
                                showOptionsMenu = false
                                showSettingsSheet = true
                            }
                        )
                    }
                }
            )
        }
    ) {
        if (showSettingsSheet) {
            SettingsSheet(
                onDismissRequest = {
                    showSettingsSheet = false
                },
                onThemeChangedRequest = { item ->
                    scope.launch {
                        delay(500)
                        SettingsManager.updateTheme(item)
                    }
                },
                onLanguageChangedRequest = { item ->
                    scope.launch {
                        delay(500)
                        SettingsManager.updateLanguage(item)
                    }
                }
            )
        }
        Column(
            modifier = Modifier
                .padding(it)
                .padding(Dimens.large)
        ) {
            CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Rtl) {
                OutlinedTextField(
                    value = input,
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    onValueChange = { value ->
                        input = value
                    },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(
                        fontFamily = vazirmatnFontFamily()
                    ),
                )
            }
            Spacer(Modifier.height(Dimens.small))
            CompositionLocalProvider(LocalLayoutDirection provides LayoutDirection.Ltr) {
                OutlinedTextField(
                    value = output,
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f),
                    readOnly = true,
                    onValueChange = {},
                    textStyle = MaterialTheme.typography.bodyLarge.copy(
                        fontFamily = montserratFontFamily()
                    )
                )
            }
            Spacer(Modifier.height(Dimens.large))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(Dimens.small)
            ) {
                ExtendedFloatingActionButton(
                    modifier = Modifier
                        .weight(1f)
                        .handPointerIcon(),
                    contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
                    containerColor = MaterialTheme.colorScheme.secondaryContainer,
                    icon = {
                        Icon(
                            painter = painterResource(Res.drawable.ic_copy),
                            contentDescription = stringResource(Res.string.action_copy)
                        )
                    },
                    text = {
                        Text(
                            text = stringResource(Res.string.action_copy)
                        )
                    },
                    onClick = {
                        clipboardManager.setText(buildAnnotatedString {
                            append(output)
                        })
                    }
                )
                if (Utils.isShareSupported()) {
                    ExtendedFloatingActionButton(
                        modifier = Modifier
                            .weight(1f)
                            .handPointerIcon(),
                        icon = {
                            Icon(
                                painter = painterResource(Res.drawable.ic_share),
                                contentDescription = stringResource(Res.string.action_share)
                            )
                        },
                        text = {
                            Text(
                                text = stringResource(Res.string.action_share)
                            )
                        },
                        onClick = {
                            val result = Utils.share(output)
                            if (!result) {
                                scope.launch {
                                    snackbarHostState.showSnackbar(
                                        getString(Res.string.error_something_wrong)
                                    )
                                }
                            }
                        }
                    )
                }
            }
        }
    }
}
